import { Text, Divider, useToast } from '@afterpaytouch/core'
import { useTranslation } from '../../utils/convergence'
import React, { useContext, useEffect } from 'react'
import {
  DisabledModal,
  Head,
  BillingAgreementMessage,
  Donation,
  ModuleContainer,
  ProfileCard,
  ShippingMessage,
  ShippingSummary,
  OrderSummary,
  OAuthLoginConsentModal,
  PaymentScheduleContainer,
  PaymentOptionsErrorModal,
  SummaryNotification,
  BiometricRegistrationModal,
  SummaryCtaFooter,
  PPAModal,
  SummaryNotificationContext,
  SummaryRouteGuard,
  NoErrorStatusGuard,
  StartedStatusGuard,
} from '../../component'
import { useFlag } from '@afterpaytouch/feature'
import { ApplePayStatus, CheckoutStatus, PaymentMethodType, Features, nonShippingInfoAddressModes, getPayIn2Experiment, CheckoutPage } from '../../model'
import { PaymentMethodTypes, TrackingEvent } from '../../model/monitoring'
import {
  selectPurchaseMethod,
  useAppDispatch,
  useApplePay,
  useApplePayState,
  useCheckoutError,
  useCheckoutState,
  useIsDeferredShipping,
  useIsExpress,
  useIsReadyToConfirm,
  useIsShippingOptionValid,
  useSelectedPaymentMethod,
  useIsBillingAgreementApprovalProcess,
  useIsTrustedCheckout,
  useAuthentication,
  useIsPickup,
  useShouldShowCashAppTooltip,
  useInitialData,
  useOAuthAuthorizationFlow,
  useExperiment,
  shouldShowOAuthConsentForm,
  acceptOAuthConsent,
  declineOAuthConsent,
  setStartConfirmTime,
  useIsAfterpayAppGiftCardFlow,
  resetCheckoutError,
  useSummaryLayout,
  useIsUpdatePreferredPaymentDay,
} from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { create } from 'react-modal-promise'
import { TrackPageViewed, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { usePostCheckoutTriggerExperiment } from '../../utils/post-checkout'
import { FirstPaymentType } from '@afterpaytouch/portal-api'
import { SIZES } from '../../component/ModuleContainer/types'
import { useConfirmCheckoutFinaliser } from '../../state/checkout/useConfirmCheckoutFinaliser'

export const BronteSummaryPage: CheckoutPage = () => {
  const { notification, setClicked, clicked } = useContext(SummaryNotificationContext)
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { status, firstPaymentType, error } = useCheckoutState()
  const isExpress = useIsExpress()
  const selectedPaymentMethod = useSelectedPaymentMethod()
  const applepay = useApplePayState()
  const ready = useIsReadyToConfirm()
  const applePayHelper = useApplePay()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const checkoutError = useCheckoutError()
  const isDeferredShipping = useIsDeferredShipping()

  const shouldUpdatePreferredPaymentDay = useIsUpdatePreferredPaymentDay()
  const isShippingOptionValid = useIsShippingOptionValid()

  const { shouldShowCashAppAccountLinkingToast, saveToTags } = useShouldShowCashAppTooltip()
  const { addressMode } = useInitialData()
  const cashTooltipExperiment = useExperiment('CheckoutSummaryCashTooltip')

  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()
  const { sectionHeadingSize, textSize } = useCheckoutSizes()
  const isPickup = useIsPickup()

  // post checkout trigger experiment
  usePostCheckoutTriggerExperiment()

  const isAfterpayAppGiftCardFlow = useIsAfterpayAppGiftCardFlow()

  const isSummaryButtonDisabled = (!ready || clicked || selectedPaymentMethod === undefined || notification?.severe) ?? false

  // PayIn2 experiment
  const payIn2ExperimentValue = useFlag(Features.CHECKOUT_PAY_IN_2_AMOUNT, -1)
  useExperiment('PayIn2Experiment', !isExpress, getPayIn2Experiment(payIn2ExperimentValue))

  // Summary Page Improvements experiment
  const { variant: summaryVariant, showProfileCard: summaryVariantShowProfileCard } = useSummaryLayout()
  const showProfileCard = status >= CheckoutStatus.Starting && summaryVariantShowProfileCard

  const toast = useToast()

  useEffect(() => {
    if (error !== undefined && error.extra !== undefined) {
      const { extra } = error
      if (extra.isEFAFinished) {
        dispatch(resetCheckoutError())
      }
    }
  }, [error])

  useEffect(() => {
    if (shouldShowCashAppAccountLinkingToast) {
      toast({
        message: () => (
          <>
            <Text size={textSize} bold>
              {t('common:firstPaymentWithCash:heading')}
            </Text>
            <Text size={textSize}>
              {cashTooltipExperiment.control ? t('common:firstPaymentWithCash:content') : t('common:firstPaymentWithCash:contentExperiment')}
            </Text>
          </>
        ),
        kind: 'Success',
        toastTime: 5000,
        swipeToDismiss: true,
      })
      saveToTags()
    }
  }, [shouldShowCashAppAccountLinkingToast, saveToTags])

  const { authenticated } = useAuthentication()
  const isTrusted = useIsTrustedCheckout()
  // Logout experiment removed. More info: https://square.slack.com/archives/C03282XR4D7/p1673242190900099?thread_ts=1673230597.741079&cid=C03282XR4D7
  const displayLogout = false

  // OAuth
  const { oAuthAuthorization } = useOAuthAuthorizationFlow()
  const showPersistentLoginConsentForm = create(({ isOpen, onResolve, onReject }) => (
    <OAuthLoginConsentModal show={isOpen} onDecline={onReject} onAccept={onResolve} />
  ))

  // This is purely for analytics and should not be used elsewhere. The FE needs to differentiate between no downpayment and some downpayment but amplitude does not care.
  const analyticsFirstPaymentType = firstPaymentType !== undefined && firstPaymentType !== '' ? firstPaymentType : FirstPaymentType.PaymentUpfront
  const moduleContainerStyle = {
    ...{ padding: { vertical: SIZES.LARGE, horizontal: SIZES.XLARGE } },
    reducedHeight: isAfterpayAppGiftCardFlow,
  }

  const finaliseCheckout = useConfirmCheckoutFinaliser({
    t,
    isTrusted,
    oAuthAuthorization,
    shouldUpdatePreferredPaymentDay,
    onBeforeNavigate: () => setClicked(false),
  })

  const handleConfirmClick = async (): Promise<void> => {
    if (isSummaryButtonDisabled) {
      logEvent(TrackingEvent.PRESSED_SUMMARY_PAGE_CONFIRM_BUTTON_DISABLED)
      return
    }

    setClicked(true)
    logEvent(TrackingEvent.PRESSED_SUMMARY_CONFIRM_BUTTON, {
      paymentMethodType: PaymentMethodTypes[selectedPaymentMethod?.type as PaymentMethodType],
    })

    dispatch(setStartConfirmTime(performance.now()))

    await dispatch(selectPurchaseMethod(selectedPaymentMethod))

    if (selectedPaymentMethod?.type === PaymentMethodType.APPLE_PAY) {
      applePayHelper.initialise(t, logEvent)
      try {
        await applePayHelper.startSession()
      } catch (e) {
        return
      }
    }

    await finaliseCheckout()
  }

  const pageTitle = `${t('summary:reviewOrder')} | ${t('common:brandName')}`

  useEffect(() => {
    if (applepay.status === ApplePayStatus.Cancelled) {
      setClicked(false)
    }
  }, [applepay.status])

  useEffect(() => {
    if (status >= CheckoutStatus.Started && !ready) {
      logEvent(TrackingEvent.CHECKOUT_CONFIRM_DISABLED, {
        checkoutStatus: status,
        checkoutError: checkoutError?.error,
        isShippingOptionValid,
      })
    }
  }, [ready])

  useEffect(() => {
    const showOauthConsent = async (): Promise<void> => {
      const shouldShowOAuthConsent = await dispatch(shouldShowOAuthConsentForm)
      await (shouldShowOAuthConsent
        ? showPersistentLoginConsentForm()
            .then(() => {
              dispatch(acceptOAuthConsent)
            })
            .catch(() => {
              dispatch(declineOAuthConsent)
            })
        : Promise.resolve())
    }
    showOauthConsent()
  }, [])

  return (
    <SummaryRouteGuard>
      <BiometricRegistrationModal />
      <NoErrorStatusGuard>
        <Head title={pageTitle} />
        <PaymentOptionsErrorModal />
        {showProfileCard && (
          <ModuleContainer {...moduleContainerStyle}>
            <ProfileCard allowLogout={displayLogout} authLogout={authenticated} />
          </ModuleContainer>
        )}
        <StartedStatusGuard>
          {isExpress && (
            <>
              {!isDeferredShipping && (
                <Text size={sectionHeadingSize} renderAs='div' bold>
                  {isPickup ? t('common:orderCollectionDetails') : t('common:chooseShippingOptions')}
                </Text>
              )}
              <ShippingSummary />
            </>
          )}
          {!isBillingAgreementApprovalProcess && (
            <>
              <OrderSummary />
              {!isAfterpayAppGiftCardFlow && <Divider kind='Hairline' />}
              {!nonShippingInfoAddressModes.includes(addressMode) && <ShippingMessage />}
            </>
          )}
          <PaymentScheduleContainer isSummary testNameSpace={'summary-page'} />
          <SummaryNotification />
          <BillingAgreementMessage />
          <PPAModal />
          <Donation />
          <SummaryCtaFooter clicked={clicked} isSummaryButtonDisabled={isSummaryButtonDisabled} notification={notification} onConfirm={handleConfirmClick} />
          <TrackPageViewed
            eventProperties={{
              firstPaymentType: analyticsFirstPaymentType,
              summaryPageVersion: summaryVariant === 'E' ? 'current ap v0' : 'legacy',
            }}
          />
          {clicked && <DisabledModal />}
        </StartedStatusGuard>
      </NoErrorStatusGuard>
    </SummaryRouteGuard>
  )
}
